import React, { Dispatch, SetStateAction, useEffect } from 'react';
import useModal from '@/hooks/useModal';
import { Button, Typography } from '@/components/common';
import { ModalHeader } from '../../modals/ModalHeader';
import { useNotification } from '@/hooks';
import { useRouter } from 'next/router';
import { actionRequiredService, GET_DOCUMENT_SETTLEMENT } from '@/api';
import { formatErrorMessage } from '@/lib';
import Link from 'next/link';
import { InputText } from '@/components/form';
import { useForm, useWatch } from 'react-hook-form';
import clsx from 'clsx';
import { ActionRequired, SettlementPayload } from '@/interfaces';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import { getCurrentFormatDate } from '@/utils';

interface ModalProps {
	actionRequiredId: number;
	caseName: string;
	isOpen: boolean;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	actionRequiredList: ActionRequired[] | undefined;
	refetch: () => void;
}

export const ModalAcceptSettlement: React.FC<ModalProps> = ({
	actionRequiredId,
	caseName,
	isOpen,
	setIsModalOpen,
	actionRequiredList,
	refetch,
}) => {
	const rules = {
		name: {
			required: { value: true, message: 'Please fill out the required field.' },
			pattern: {
				value: /^[A-Za-z\s]+$/i,
				message: 'Only alphabetic characters accepted',
			},
			maxLength: {
				value: 85,
				message: 'Your name has to have less than 65 characters',
			},
		},
		signature: {
			required: { value: true, message: 'Please fill out the required field.' },
			pattern: {
				value: /^[A-Za-z\s]+$/i,
				message: 'Only alphabetic characters accepted',
			},
			maxLength: {
				value: 45,
				message: 'Your name has to have less than 65 characters',
			},
		},
	};

	const router = useRouter();
	const { pathname, query } = router;
	const { Modal, show, hide } = useModal();
	const { toastError, toastSuccess } = useNotification();
	const [isPendingSign, setIsPendingSign] = React.useState(true);
	const [isLoading, setIsLoading] = React.useState(false);
	const [settlement, setSettlement] = React.useState<
		ActionRequired | undefined
	>();
	const [settlementDocument, setSettlementDocument] = React.useState<
		string | undefined
	>();
	const {
		reset,
		register,
		trigger,
		getValues,
		setValue,
		formState: { errors },
		control,
		clearErrors,
	} = useForm();

	const formValues = useWatch({
		control,
	});

	const { data: document } = useQuery({
		queryKey: [GET_DOCUMENT_SETTLEMENT],
		queryFn: () =>
			actionRequiredService.getDocumentSettlement(
				String(settlement?.application_id)
			),
		enabled: !!settlement?.application_id,
	});

	useEffect(() => {
		if (isOpen) {
			show();
			const currentSettlement = actionRequiredList?.find(
				({ id }) => actionRequiredId === id
			);
			setSettlement(currentSettlement);
			setIsPendingSign(
				currentSettlement?.action_type === 'SETTLEMENT_WITH_SIGN'
			);
		}
	}, [isOpen]);

	useEffect(() => {
		clearErrors();
		setIsLoading(false);
	}, [formValues]);

	useEffect(() => {
		if (document?.data) {
			setSettlementDocument(document.data.file_path);
		}
	}, [document]);

	const deleteQuery = (): void => {
		delete query['type'];
		router.replace({
			pathname,
			query,
		});
	};

	const handleCloseModal = (): void => {
		hide();
		setIsModalOpen(false);
		reset();
	};

	const setTimeStamp = useDebouncedCallback(() => {
		const formattedDate = getCurrentFormatDate();
		setValue('date', formattedDate);
	}, 500);

	const handleSave = async (approvalStatus: 'decline' | 'accept') => {
		setIsLoading(true);
		if (!isPendingSign) {
			noSignSettlement(approvalStatus);
		}

		if (isPendingSign) {
			if (approvalStatus === 'accept') {
				const isValid = await trigger();
				if (isValid) signSettlement(approvalStatus);
			} else {
				signSettlement(approvalStatus);
			}
		}
	};

	const noSignSettlement = async (approvalStatus: 'decline' | 'accept') => {
		try {
			const payload: SettlementPayload = {
				type_settlement: 'SETTLEMENT_WITHOUT_SIGN',
				is_confirm: approvalStatus === 'accept',
			};

			const message =
				approvalStatus === 'accept'
					? 'You have successfully accepted your settlement.'
					: 'You have successfully declined the settlement.';

			await actionRequiredService.resolveSettlement(
				String(settlement?.application_id),
				payload
			);
			toastSuccess(message);
			complete();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoading(false);
		}
	};

	const signSettlement = async (approvalStatus: 'decline' | 'accept') => {
		const values = getValues();
		try {
			const payload: SettlementPayload = {
				type_settlement: 'SETTLEMENT_WITH_SIGN',
				name: values.name,
				sign: values.signature,
				is_confirm: approvalStatus === 'accept',
			};
			const message =
				approvalStatus === 'accept'
					? 'You have successfully accepted your settlement.'
					: 'You have successfully declined the settlement.';
			await actionRequiredService.resolveSettlement(
				String(settlement?.application_id),
				payload
			);
			toastSuccess(message);
			complete();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoading(false);
		}
	};

	const complete = () => {
		refetch();
		handleCloseModal();
		setIsModalOpen(false);
		deleteQuery();
		reset();
	};

	return (
		<Modal
			classNameCardContainer="max-w-[532px]"
			withXButton
			title={<ModalHeader title="Accept your Settlement" />}
			onClose={handleCloseModal}
		>
			<div className="mt-2">
				<div className="flex flex-col gap-8">
					<Typography type="regular-16" className="text-center">
						We have good news-your case: {caseName} has been resolved!
					</Typography>
					<Link
						href={settlementDocument ?? ''}
						className="px-12"
						target="_blank"
					>
						<Typography
							type="regular-16"
							className="text-center text-blue-link !underline"
						>
							Click here to review and download the settlement agreement.
						</Typography>
					</Link>
				</div>
				<Typography
					type="semibold-18"
					className={clsx('uppercase text-left mt-14', {
						hidden: !isPendingSign,
					})}
				>
					Accepted And Agreed To:
				</Typography>
				<form className="mt-4 space-y-4">
					{isPendingSign && (
						<>
							<InputText
								name="name"
								title="Name"
								rules={rules.name}
								register={register}
								error={errors?.name}
								placeholder="First name and Last name"
								classNameInput="!bg-gray-5 placeholder:text-[#6E6A73]"
							/>
							<InputText
								name="signature"
								title="Signature"
								register={register}
								rules={rules.signature}
								error={errors?.signature}
								onBlur={() => {
									const sign = getValues('signature');
									if (sign) {
										setTimeStamp();
									}
								}}
								placeholder="Alex Cullen"
								classNameInput="!bg-gray-5 placeholder:text-[#0D061614] !font-praise !text-2xl"
							/>
							<InputText
								readOnly
								name="date"
								title="Date"
								register={register}
								placeholder="mm/dd/yyyy"
								classNameInput="!bg-gray-5 placeholder:text-[#6E6A73]"
							/>
						</>
					)}
					<div className="flex gap-4 justify-center mt-8">
						<Button
							disabled={isLoading}
							size="medium"
							decoration="danger-outline"
							onClick={() => handleSave('decline')}
							className="!py-4 !bg-transparent"
						>
							Decline
						</Button>
						<Button
							disabled={isLoading}
							size="medium"
							decoration="primary"
							className="!py-4"
							onClick={() => handleSave('accept')}
						>
							Accept
						</Button>
					</div>
				</form>
			</div>
		</Modal>
	);
};
